import React, {
  useState,
  useCallback,
  useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import {
  Card, Button, Accordion, Icon, Input,
} from 'semantic-ui-react';
import {
  Other, OpenAPI,
} from 'simplydo/interfaces';
import {
  useAppSelector,
} from 'store';
import { EmptyBox } from 'components/lib/UI';
import api from 'api';
import util from 'utils/utils';
import toast from 'react-hot-toast';
import useTheme from 'theme/useTheme';
import { UserChip, SimplyDoChip } from 'components/lib/Chips';
import useThrottle from 'utils/useThrottle';

const templates = [
  {
    _id: '3is',
    name: 'The "3 Is"',
    description: 'A short template designed to quickly collect ideas for internal innovation. Includes a self-assessment rating against Importance, Impact, and ease of Implementation.',
    image: util.getCdnFile('images/format_3is.png'),
    disableAttachments: true,
    body: [
      {
        name: 'The idea',
        fields: [
          {
            characterLimit: '250',
            id: 'overview',
            title: 'What is your idea about?',
            description: 'Briefly describe your idea as you see it.',
            type: 'longtext',
          },
          {
            id: 'importance',
            title: 'How important is this idea to you?',
            description: '10 = Very Important',
            type: 'slider',
          },
          {
            id: 'implementation',
            title: 'How easy would it be to implement?',
            description: '10 = Very easy',
            type: 'slider',
          },
          {
            id: 'impact',
            title: 'How much impact could it have?',
            description: '10 = Game changing',
            type: 'slider',
          },
          {
            id: 'image',
            title: 'Please upload an image of your idea ',
            description: 'Optional ',
            type: 'image',
          },
        ],
      },
    ],
  },
  // {
  //   _id: 'challengeCapture',
  //   name: 'Challenge capture',
  //   description: 'A template designated for capturing a broader scope of active issues that should be addressed. The results are reflective of potential future challenges.',
  //   image: util.getCdnFile('images/format_3is.png'),
  //   tagBeforeSubmission: true,
  //   body: [
  //     {
  //       name: 'Scope of the issue',
  //       fields: [
  //         {
  //           id: 'challengeCapture-information',
  //           content: 'Please keep in mind that this is a space to explain and demonstrate problems that you would like to see solved. Try to formalise your concerns without including your proposed solutions at this point.',
  //           type: 'infotext',
  //         },
  //         {
  //           wordLimit: '10',
  //           id: 'challengeCapture-summary',
  //           title: 'What is the issue?',
  //           description: 'In one sentence, summarise the overarching issue.',
  //           type: 'shorttext',
  //           required: true,
  //         },
  //         {
  //           id: 'challengeCapture-description',
  //           title: 'Filling in the blanks',
  //           description: 'Help us understand the problem you are facing in more detail.',
  //           type: 'richtext',
  //           required: true,
  //         },
  //         {
  //           id: 'challengeCapture-instructions',
  //           title: 'Instructing the helpers (optional)',
  //           description: 'What advice would you give to people involved in solving this challenge? Are there any steps to be taken or other information they could reference?',
  //           type: 'richtext',
  //         },
  //         {
  //           id: 'challengeCapture-requirements',
  //           title: 'What should be included? (optional)',
  //           description: 'Do you have any specific thoughts about the types of things people could include in their ideas to help solve this challenge?',
  //           type: 'richtext',
  //         },
  //         {
  //           id: 'challengeCapture-helpfulTips',
  //           title: 'Any hints? (optional)',
  //           description: 'Do you have any further tips or can you provide any additional context to help those looking to solve this challenge?',
  //           type: 'richtext',
  //         },
  //       ],
  //     },
  //   ],
  // },
  {
    _id: '6cs',
    name: 'The "6 Cs"',
    description: 'This format enables participants to create an early-stage business plan following the academically-robust "6 Cs" canvas covering Concept through to Cash.',
    image: util.getCdnFile('images/format_6cs.png'),
    disableAttachments: true,
    body: [
      { include: 'concept' },
      { include: 'customers' },
      { include: 'competitors' },
      { include: 'compatibility' },
      { include: 'contents' },
      { include: 'cash' },
    ],
  },
  {
    _id: 'scratch',
    name: 'Start from scratch',
    description: 'None of these suit? No problem - you can craft your own format from scratch using our format builder.',
    image: util.getCdnFile('images/format_scratch.png'),
    disableAttachments: true,
    body: [
      {
        name: 'The idea',
        fields: [
        ],
      },
    ],
  },
];

type IIdeaTemplateChooser = {
  onTemplateSelected: (template: any) => void,
}

type IChallengeSearchState = {
  page: number,
  limit: number,
  query: string,
  withProjectBoard: boolean,
}

const IdeaTemplateChooser = ({
  onTemplateSelected,
}: IIdeaTemplateChooser) => {
  const [_loading, setLoading] = useState<boolean>(false);
  const [challenges, setChallenges] = useState<Other.IChallenge[]>([]);
  const [nextPageAvailable, setNextPageAvailable] = useState<boolean>(false);
  const [prevPageAvailable, setPrevPageAvailable] = useState<boolean>(false);
  const [accordionIndex, setAccordionIndex] = useState<number>(-1);
  const [selectedChallenge, setSelectedChallenge] = useState<string>('');
  const [challengeSearchState, setChallengeSearchState] = useState<IChallengeSearchState>({
    page: 1,
    limit: 9,
    query: '',
    withProjectBoard: false,
  });
  const user: OpenAPI.GET<'/users/me'> = useAppSelector((state) => state.user);
  const { t } = useTranslation();
  const theme = useTheme();

  const userId = user?._id;
  const getChallenges = useThrottle(() => {
    setLoading(true);
    setSelectedChallenge('');
    api.users.getChallenges(userId, challengeSearchState, (data) => {
      setChallenges(data.challenges);
      setNextPageAvailable(data.nextPageAvailable);
      setPrevPageAvailable(data.prevPageAvailable);
      setLoading(false);
    }, (err) => {
      toast.error(err.message);
      setLoading(false);
    });
  }, 500, [userId, challengeSearchState]);

  useEffect(() => {
    getChallenges();
  }, [getChallenges]);

  const onClickDefaultTemplate = useCallback((template) => {
    onTemplateSelected(template);
    setSelectedChallenge(template._id);
  }, [onTemplateSelected]);

  const onClickChallenge = useCallback((clickedChallenge) => {
    setSelectedChallenge(clickedChallenge._id);
    api.challenges.getTemplate(clickedChallenge._id, (newTemplate) => {
      if (newTemplate.body) {
        onTemplateSelected(newTemplate);
      } else {
        toast.error('Challenge has no valid template.');
      }
    }, () => { }, null);
  }, [onTemplateSelected]);

  const handleAccordionClick = useCallback((e, titleProps) => {
    if (!titleProps) return;
    const { index } = titleProps;
    const newIndex = accordionIndex === index ? -1 : index;
    setAccordionIndex(newIndex);
  }, [accordionIndex]);

  const sixCsEnabled = user?.organisation?.enabledFeatures?.includes('6cs');
  // if 6cs is not enabled we filter it from the list of templates
  const filteredTemplates = sixCsEnabled ? templates : templates.filter((template) => template._id !== '6cs');
  return (
    <div>
      <h3>
        {!selectedChallenge ? (<Icon name={'exclamation circle'} color={'red'} />) : null}{t('challenge.templateChooser.title')}
      </h3>
      <Card.Group itemsPerRow={filteredTemplates.length} stackable>
        {filteredTemplates.map((template, i) => {
          // Temporarily disable challenge capture for regular users/admins
          if (template._id === 'challengeCapture' && !util.isInRole(user, 'super.viewDashboard')) return null;
          return (
            <Card
              raised
              key={i}
            >
              {template.image
                && (
                  <div style={{
                    height: 150, backgroundPosition: 'center center', backgroundSize: 'cover', backgroundImage: `url(${template.image})`,
                  }} />
                )
              }
              <Card.Content>
                <Card.Header>{template.name}</Card.Header>
                <Card.Meta>{template.description}</Card.Meta>
              </Card.Content>
              <Card.Content extra>
                <SimplyDoChip style={undefined} colour={undefined} />
              </Card.Content>
              <Card.Content extra>
                <Button
                  fluid
                  primary
                  title={template.name}
                  content='Use this format'
                  onClick={() => onClickDefaultTemplate(template)}
                />
              </Card.Content>
            </Card>
          );
        })}
      </Card.Group>

      {(challenges.length > 0 || !!challengeSearchState.query)
        ? (
          <Accordion style={{ marginTop: 20 }}>
            <Accordion.Title active={accordionIndex === 0} index={0} onClick={handleAccordionClick}>
              <h4><Icon name='dropdown' />Re-use a format from one of your existing challenges</h4>
            </Accordion.Title>
            <Accordion.Content
              active={accordionIndex === 0} index={0} onClick={handleAccordionClick}
            >
              <Input
                placeholder='Search for a challenge..'
                onChange={(e, { value }) => setChallengeSearchState((prevState) => ({ ...prevState, page: 1, query: value }))}
                style={{ marginBottom: 10 }}
                value={challengeSearchState.query}
              />
              {challenges?.length
                ? (
                  <Card.Group itemsPerRow={theme.sizes.isComputer ? 3 : 2} centered>
                    {challenges.map((c) => (
                      <Card raised key={c._id}>
                        <div style={{
                          height: 150, backgroundPosition: 'center center', backgroundSize: 'cover', backgroundImage: `url(${util.challengeImage(c)})`,
                        }} />
                        <Card.Content>
                          <Card.Header>{c.name}</Card.Header>
                          <Card.Meta>{t('challenge.templateChooser.copyFromChallenge')}</Card.Meta>
                          <Card.Meta style={{ marginTop: 10 }}>
                            <Link className='with-border' to={`/challenges/${c._id}/format`} target="_blank">View format</Link>
                          </Card.Meta>
                        </Card.Content>
                        <Card.Content extra>
                          { /* @ts-ignore */}
                          <UserChip user={{ ...user, emails: null }} />
                        </Card.Content>
                        <Card.Content extra>
                          <Button
                            fluid
                            primary
                            content='Use this format'
                            onClick={() => onClickChallenge(c)}
                          />
                        </Card.Content>
                      </Card>
                    ))}
                  </Card.Group>
                ) : (
                  <EmptyBox title={undefined} message={undefined} style={undefined}>No challenges found</EmptyBox>
                )
              }
              {nextPageAvailable || prevPageAvailable
                ? (
                  <div
                    style={{
                      display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center', marginTop: 10,
                    }}
                  >
                    {prevPageAvailable
                      ? (
                        <Button
                          size='small'
                          content='Prev page'
                          onClick={() => setChallengeSearchState((prevState) => ({
                            ...prevState, page: prevState.page - 1,
                          }))}
                        />
                      ) : <div />
                    }
                    {nextPageAvailable
                      ? (
                        <Button
                          size='small'
                          content='Next page'
                          onClick={() => setChallengeSearchState((prevState) => ({
                            ...prevState, page: prevState.page + 1,
                          }))}
                        />
                      ) : null
                    }
                  </div>
                ) : null
              }
            </Accordion.Content>
          </Accordion>
        ) : null
      }
    </div>
  );
};

const mapStateToProps = (state) => ({ user: state.user });

export default (connect(mapStateToProps)(IdeaTemplateChooser));
